import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MfaPolicyCard } from '@/features/enrolment/components/MfaPolicyCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
    Shield,
    Clock,
    RefreshCw,
    MonitorPause
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api/axios';
import { usePopulatePermissions } from '@/features/admin/hooks';
import { adminApi } from '@/features/admin/api';
import { useToast } from '@/hooks/use-toast';

export function SettingsPage() {
    const { t } = useTranslation();
    const populatePermissions = usePopulatePermissions();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [vaultRetention, setVaultRetention] = useState<number>(30);
    const [objectRetention, setObjectRetention] = useState<number>(30);

    const { data: retentionDefaults } = useQuery({
        queryKey: ['retention-defaults'],
        queryFn: async () => {
            const res = await adminApi.getRetentionDefaults();
            return res.data.data;
        },
    });

    useEffect(() => {
        if (retentionDefaults) {
            setVaultRetention(retentionDefaults.vault);
            setObjectRetention(retentionDefaults.object);
        }
    }, [retentionDefaults]);

    const objectMax = retentionDefaults?.objectMax ?? 30;

    // ── Idle behaviour ────────────────────────────────────────────────────────────
    const [screensaverEnabled, setScreensaverEnabled] = useState(true);
    const [screensaverMinutes, setScreensaverMinutes] = useState(10);
    const [logoutMinutes, setLogoutMinutes] = useState(480);

    const { data: idleSettings } = useQuery({
        queryKey: ['idle-settings'],
        queryFn: async () => {
            const res = await api.get('/api/admin/idle-settings');
            const body = res.data as any;
            return (body?.data ?? body) as {
                screensaverEnabled: boolean; screensaverMinutes: number; logoutMinutes: number;
                limits?: { screensaverMin: number; screensaverMax: number; logoutMin: number; logoutMax: number };
            };
        },
    });

    useEffect(() => {
        if (!idleSettings) return;
        setScreensaverEnabled(idleSettings.screensaverEnabled);
        setScreensaverMinutes(idleSettings.screensaverMinutes);
        setLogoutMinutes(idleSettings.logoutMinutes);
    }, [idleSettings]);

    const saveIdle = useMutation({
        mutationFn: async () => api.put('/api/admin/idle-settings', {
            screensaverEnabled, screensaverMinutes, logoutMinutes,
        }),
        onSuccess: () => {
            // Every client reads this key, so invalidating it applies the change without a reload.
            queryClient.invalidateQueries({ queryKey: ['idle-settings'] });
            toast({ title: t('settings.idleSaved', 'Screen lock settings saved') });
        },
        onError: (err: any) => {
            // The server rejects a logout shorter than the screensaver; show its reason rather
            // than a generic failure, because the fix is specific.
            const msg = err?.response?.data?.message || err?.response?.data?.error
                || t('settings.idleSaveError', 'Failed to save screen lock settings');
            toast({ title: msg, variant: 'destructive' });
        },
    });

    const logoutTooShort = logoutMinutes <= screensaverMinutes;

    const saveRetention = useMutation({
        mutationFn: () => adminApi.updateRetentionDefaults({
            vault: vaultRetention,
            object: objectRetention,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['retention-defaults'] });
            toast({ title: t('settings.retentionSaved', 'Retention defaults saved') });
        },
        onError: () => {
            toast({ title: t('settings.retentionSaveError', 'Failed to save retention defaults'), variant: 'destructive' });
        },
    });

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl lg:text-2xl font-bold">{t('settings.title', 'System Settings')}</h1>
                    <p className="text-muted-foreground">
                        {t('settings.description', 'Configure system-wide settings and integrations. Admin access required.')}
                    </p>
                </div>
            </div>

            {/* Every user needs a second factor: the switch, and who is not ready for it. */}
            <MfaPolicyCard />

            {/* Security & Access Settings */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        {t('settings.securityTitle', 'Security & Access')}
                    </CardTitle>
                    <CardDescription>
                        {t('settings.securityDescription', 'Configure system security and access controls.')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="ssl-certificates">{t('settings.sslCertificates', 'SSL Certificates')}</Label>
                            <p className="text-sm text-muted-foreground">
                                {t('settings.sslCertificatesDescription', 'Manage SSL certificates for secure communications.')}
                            </p>
                        </div>
                        <Button variant="outline" size="sm">
                            {t('settings.manage', 'Manage')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Retention Defaults */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        {t('settings.retentionTitle', 'Retention Defaults')}
                    </CardTitle>
                    <CardDescription>
                        {t('settings.retentionDescription', 'Default retention period (in days) applied to newly created vaults and objects.')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                            <Label htmlFor="vault-retention">{t('settings.defaultVaultRetention', 'Default Vault Retention')}</Label>
                            <p className="text-sm text-muted-foreground">
                                {t('settings.defaultVaultRetentionDescription', 'Number of days new vaults retain backups by default.')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Input
                                id="vault-retention"
                                type="number"
                                min={1}
                                className="w-24"
                                value={vaultRetention}
                                onChange={(e) => setVaultRetention(Math.max(1, parseInt(e.target.value) || 0))}
                            />
                            <span className="text-sm text-muted-foreground">{t('settings.days', 'days')}</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                            <Label htmlFor="object-retention">{t('settings.defaultObjectRetention', 'Default Object Retention')}</Label>
                            <p className="text-sm text-muted-foreground">
                                {t('settings.defaultObjectRetentionDescription', 'Number of days new objects retain backups by default (maximum {{max}}).', { max: objectMax })}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Input
                                id="object-retention"
                                type="number"
                                min={1}
                                max={objectMax}
                                className="w-24"
                                value={objectRetention}
                                onChange={(e) => setObjectRetention(Math.min(objectMax, Math.max(1, parseInt(e.target.value) || 0)))}
                            />
                            <span className="text-sm text-muted-foreground">{t('settings.days', 'days')}</span>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            onClick={() => saveRetention.mutate()}
                            disabled={saveRetention.isPending}
                        >
                            {saveRetention.isPending ? (
                                <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />{t('common.saving', 'Saving...')}</>
                            ) : t('common.save', 'Save')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Screensaver and sign-out */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <MonitorPause className="h-5 w-5" />
                        {t('settings.screensaverTitle', 'Screen lock and inactivity')}
                    </CardTitle>
                    <CardDescription>
                        {t('settings.screensaverDescription',
                           'An idle screen is locked rather than signed out. Unlocking needs the same credentials as signing in, and returns you to exactly where you were — so a screen left up in an operations room keeps its view instead of dropping to a login form. Signing out is a separate, longer timeout.')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                            <Label htmlFor="screensaver-enabled">{t('settings.screensaverEnabled', 'Lock the screen when idle')}</Label>
                            <p className="text-sm text-muted-foreground">
                                {t('settings.screensaverEnabledDescription',
                                   'Hides what is on screen so it cannot be read by someone passing, and asks for your password — plus your authentication code or passkey, if your account uses one — before it comes back. The session itself keeps running.')}
                            </p>
                        </div>
                        <Switch id="screensaver-enabled" checked={screensaverEnabled}
                                onCheckedChange={setScreensaverEnabled} />
                    </div>

                    <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                            <Label htmlFor="screensaver-minutes">{t('settings.screensaverAfter', 'Lock the screen after')}</Label>
                            <p className="text-sm text-muted-foreground">
                                {t('settings.screensaverAfterDescription', 'Minutes of no mouse, keyboard or touch activity.')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Input id="screensaver-minutes" type="number"
                                   min={idleSettings?.limits?.screensaverMin ?? 1}
                                   max={idleSettings?.limits?.screensaverMax ?? 240}
                                   className="w-24" disabled={!screensaverEnabled}
                                   value={screensaverMinutes}
                                   onChange={(e) => setScreensaverMinutes(Math.max(1, parseInt(e.target.value) || 0))} />
                            <span className="text-sm text-muted-foreground">{t('settings.minutes', 'minutes')}</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                        <div className="space-y-0.5">
                            <Label htmlFor="logout-minutes">{t('settings.logoutAfter', 'Sign out after')}</Label>
                            <p className="text-sm text-muted-foreground">
                                {t('settings.logoutAfterDescription',
                                   'Minutes of inactivity before the session actually ends. Must be longer than the lock timeout, or the screen would never lock before signing out. Capped at 8 hours, which is when the sign-in expires anyway.')}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Input id="logout-minutes" type="number"
                                   min={idleSettings?.limits?.logoutMin ?? 5}
                                   max={idleSettings?.limits?.logoutMax ?? 480}
                                   className={logoutTooShort ? 'w-24 border-destructive' : 'w-24'}
                                   value={logoutMinutes}
                                   onChange={(e) => setLogoutMinutes(Math.max(1, parseInt(e.target.value) || 0))} />
                            <span className="text-sm text-muted-foreground">{t('settings.minutes', 'minutes')}</span>
                        </div>
                    </div>

                    {logoutTooShort && (
                        <p className="text-sm text-destructive">
                            {t('settings.logoutTooShort', 'The sign-out timeout must be longer than the lock timeout.')}
                        </p>
                    )}

                    <p className="text-xs text-muted-foreground">
                        {t('settings.lockNow', 'You can also lock the screen at any time from your account menu.')}
                    </p>

                    <div className="flex justify-end">
                        <Button size="sm" onClick={() => saveIdle.mutate()}
                                disabled={saveIdle.isPending || logoutTooShort}>
                            {saveIdle.isPending ? (
                                <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />{t('common.saving', 'Saving...')}</>
                            ) : t('common.save', 'Save')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* System Administration */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        {t('settings.systemAdministration', 'System Administration')}
                    </CardTitle>
                    <CardDescription>
                        {t('settings.systemAdministrationDescription', 'Administrative tools for system maintenance.')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                            <Label htmlFor="populate-permissions">{t('settings.populatePermissions', 'Populate Permissions')}</Label>
                            <p className="text-sm text-muted-foreground">
                                {t('settings.populatePermissionsDescription', 'Create and assign all required permissions for all screens to roles. This will ensure all permissions exist and are properly assigned to the admin role.')}
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => populatePermissions.mutate()}
                            disabled={populatePermissions.isPending}
                        >
                            {populatePermissions.isPending ? (
                                <>
                                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                    {t('settings.populating', 'Populating...')}
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    {t('settings.populatePermissions', 'Populate Permissions')}
                                </>
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
